import {
  ArrowBack,
  Business,
  Cancel,
  Edit,
  Email,
  Person,
  Save,
} from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { Actions, useStoreActions, useStoreState } from "easy-peasy"
import { UpdateContactInput } from "graphql/types"
import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { AppRoutes } from "store/routes"
import { StoreModel } from "store/store"

const DetailItem: React.FC<{
  label: string
  value?: string | null | boolean | React.ReactNode
  isEditable?: boolean
  name?: keyof UpdateContactInput
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  onSwitchChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  type?: "text" | "email" | "tel" | "switch"
}> = ({
  label,
  value,
  isEditable,
  name,
  onChange,
  onSwitchChange,
  type = "text",
}) => {
  if (isEditable && name && onChange) {
    if (type === "switch" && onSwitchChange) {
      return (
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch checked={!!value} onChange={onSwitchChange} name={name} />
            }
            label={label}
            sx={{ mt: 1, mb: 1 }}
          />
        </Grid>
      )
    }
    return (
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={label}
          name={name}
          value={value || ""}
          onChange={onChange}
          margin="normal"
          type={type === "switch" ? "text" : type}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
    )
  }
  return (
    <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ minWidth: "120px", fontWeight: "bold" }}
      >
        {label}:
      </Typography>
      <Typography
        component="div"
        variant="body1"
        sx={{ wordBreak: "break-word" }}
      >
        {value === null || value === undefined || value === "" ? (
          "N/A"
        ) : typeof value === "boolean" ? (
          value ? (
            <Chip label="Ja" color="success" size="small" />
          ) : (
            <Chip label="Nein" color="default" size="small" />
          )
        ) : (
          String(value)
        )}
      </Typography>
    </Grid>
  )
}

const ContactDetailsPage: React.FC = () => {
  const navigate = useNavigate()
  const { contactID: routeContactID } = useParams<{ contactID: string }>()

  const contacts = useStoreState((state: StoreModel) => state.contacts)
  const getContactByIdAction = useStoreActions(
    (actions: Actions<StoreModel>) => actions.getContactById
  )
  const updateContactAction = useStoreActions(
    (actions: Actions<StoreModel>) => actions.updateContact
  )

  const [isEditing, setIsEditing] = useState(false)
  const [editFormData, setEditFormData] = useState<Partial<UpdateContactInput>>(
    {}
  )
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { fetchContacts } = useStoreActions(
    (actions: Actions<StoreModel>) => actions
  )

  useEffect(() => {
    const loadContacts = async () => {
      try {
        await fetchContacts({ fresh: false })
      } catch (err) {
        console.error("Error loading contacts:", err)
      }
    }
    loadContacts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const contact = contacts.find(
    (c) => c.contactID === routeContactID || c.id === routeContactID
  )

  useEffect(() => {
    if (routeContactID) {
      if (!contact) {
        setLoading(true)
        console.log(
          `Contact with ID '${routeContactID}' not in local store. Fetching by ID...`
        )
        getContactByIdAction({ contactID: routeContactID, fresh: true })
          .catch((err: unknown) => {
            console.error("Error fetching contact by ID from useEffect:", err)
            setError(
              `Could not load contact details: ${(err as Error).message}`
            )
          })
          .finally(() => {
            setLoading(false)
          })
      } else if (contact && !isEditing) {
        setEditFormData({
          firstName: contact.firstName || "",
          lastName: contact.lastName || "",
          email: contact.email || "",
          phone1: contact.phone1 || "",
          title: contact.title || "",
          active: contact.active !== undefined ? contact.active : true,
        })
      }
    }
  }, [routeContactID, contact, getContactByIdAction, isEditing])

  const handleEditToggle = () => {
    if (isEditing && contact) {
      setEditFormData({
        firstName: contact.firstName || "",
        lastName: contact.lastName || "",
        email: contact.email || "",
        phone1: contact.phone1 || "",
        title: contact.title || "",
        active: contact.active !== undefined ? contact.active : true,
      })
    }
    setIsEditing(!isEditing)
    setError(null)
    setSuccess(null)
  }

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target
    setEditFormData((prev) => ({
      ...prev,
      [name]: value === "" ? null : value,
    }))
  }

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target
    setEditFormData((prev) => ({
      ...prev,
      [name]: checked,
    }))
  }

  const handleSubmitEdit = async () => {
    if (!contact || !contact.contactID) {
      setError("Kontakt Treo ID fehlt, kann nicht aktualisieren.")
      return
    }
    setIsSubmitting(true)
    setError(null)
    setSuccess(null)

    const updatePayload: UpdateContactInput = {
      contactID: contact.contactID,
      ...(editFormData.firstName !== undefined && {
        firstName: editFormData.firstName,
      }),
      ...(editFormData.lastName !== undefined && {
        lastName: editFormData.lastName,
      }),
      ...(editFormData.email !== undefined && { email: editFormData.email }),
      ...(editFormData.phone1 !== undefined && { phone1: editFormData.phone1 }),
      ...(editFormData.title !== undefined && { title: editFormData.title }),
      ...(editFormData.active !== undefined && { active: editFormData.active }),
    }

    try {
      const updatedContact = await updateContactAction(updatePayload)
      if (updatedContact) {
        setSuccess("Kontakt erfolgreich aktualisiert!")
        setIsEditing(false)
      } else {
        setError("Failed to update contact. The operation returned no data.")
      }
    } catch (err: unknown) {
      setError(
        `Update failed: ${(err as Error).message || "An unexpected error occurred."}`
      )
    }
    setIsSubmitting(false)
  }

  if (loading && !contact) {
    return (
      <Container sx={{ textAlign: "center", mt: 5 }}>
        <CircularProgress />
        <Typography>Kontaktdetails werden geladen...</Typography>
      </Container>
    )
  }

  if (!contact) {
    return (
      <Container sx={{ mt: 3 }}>
        <Alert severity={error ? "error" : "warning"}>
          {error ||
            `Contact with ID ${routeContactID} not found. It might have been deleted or the ID is incorrect.`}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(AppRoutes.CONTACTS || -1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h4" gutterBottom component="div">
            {isEditing
              ? "Kontakt bearbeiten"
              : contact.displayName ||
                `${contact.firstName} ${contact.lastName}` ||
                "Kontaktdetails"}
          </Typography>
          <Box>
            {isEditing ? (
              <>
                <Button
                  startIcon={<Save />}
                  onClick={handleSubmitEdit}
                  variant="contained"
                  color="primary"
                  sx={{ mr: 1 }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Änderungen speichern"
                  )}
                </Button>
                <Button
                  startIcon={<Cancel />}
                  onClick={handleEditToggle}
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={handleEditToggle}
                sx={{ mr: 1 }}
                disabled={true}
              >
                Kontakt bearbeiten
              </Button>
            )}
            {!isEditing && (
              <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate(AppRoutes.CONTACTS || -1)}
              >
                Back to List
              </Button>
            )}
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {error && !isSubmitting && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        <Card variant="outlined">
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Person sx={{ mr: 1 }} /> Personal Information
            </Typography>
            <Grid container spacing={isEditing ? 0 : 2} alignItems="flex-start">
              <DetailItem
                label="Vorname"
                value={isEditing ? editFormData.firstName : contact.firstName}
                isEditable={isEditing}
                name="firstName"
                onChange={handleChange}
              />
              <DetailItem
                label="Nachname"
                value={isEditing ? editFormData.lastName : contact.lastName}
                isEditable={isEditing}
                name="lastName"
                onChange={handleChange}
              />
              <DetailItem
                label="Titel"
                value={isEditing ? editFormData.title : contact.title}
                isEditable={isEditing}
                name="title"
                onChange={handleChange}
              />
              <DetailItem
                label="Aktiv"
                value={isEditing ? editFormData.active : contact.active}
                isEditable={isEditing}
                name="active"
                onSwitchChange={handleSwitchChange}
                type="switch"
              />
              {!isEditing && (
                <>
                  <DetailItem
                    label="Anzeigename"
                    value={contact.displayName}
                  />
                  <DetailItem label="Geschlecht" value={contact.gender} />
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Email sx={{ mr: 1 }} /> Contact Information
            </Typography>
            <Grid container spacing={isEditing ? 0 : 2} alignItems="flex-start">
              <DetailItem
                label="E-Mail"
                value={isEditing ? editFormData.email : contact.email}
                isEditable={isEditing}
                name="email"
                onChange={handleChange}
                type="email"
              />
              <DetailItem
                label="Telefon 1"
                value={isEditing ? editFormData.phone1 : contact.phone1}
                isEditable={isEditing}
                name="phone1"
                onChange={handleChange}
                type="tel"
              />
              {!isEditing && (
                <>
                  <DetailItem label="Telefon 2" value={contact.phone2} />
                  <DetailItem label="Telefon 3" value={contact.phone3} />
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Business sx={{ mr: 1 }} /> Company & System Information
            </Typography>
            <Grid container spacing={2}>
              <DetailItem label="Firmenname" value={contact.companyName} />
              <DetailItem
                label="Geschäftskonto (Treo ID)"
                value={contact.businessAccount}
              />
              <DetailItem label="Kontakt Treo ID" value={contact.contactID} />
              <DetailItem label="Interne GUID" value={contact.id} />
            </Grid>
          </CardContent>
        </Card>
      </Paper>
    </Container>
  )
}

export default ContactDetailsPage
